import Vue from 'vue';
import { apolloProvider } from '@/graphql/vueApollo'; // GraphQL connection
import logger from 'client-logger';
import formatGraphQlError from '@/utilities/formatGraphQlError';
import { FORBIDDEN } from '@/utilities/errorCodes';

// Our queries
import GET_PERMISSIONS_BY_ROLE_ID from '@/graphql/queries/getPermissionsByRoleId.gql';
import GET_ALL_ROLES from '@/graphql/queries/getAllRoles.gql';

const apollo = apolloProvider.defaultClient;

// Constants
const LOCAL_STORAGE_PERMISSIONS = 'allPermissionsByRole';
const LOCAL_STORAGE_ROLES = 'roleDefs';

// An interface for getting, querying, and mutating our data. All methods with GraphQL queries by default return cached values, but can be forced to re-fetch.
const Role = new Vue({
	data() {
		return {
			allPermissionsByRole: {},
			roleDefs: []
		};
	},
	computed: {
		// Returns the list of permissions specified by the ID.
		permissionsByRole() {
			return roleId => {
				if (!this.allPermissionsByRole[roleId] || this.allPermissionsByRole === {}) {
					apollo
						.query({
							query: GET_PERMISSIONS_BY_ROLE_ID,
							variables: {
								roleId
							}
						})
						.then(response => {
							this.setPermissionsByRoleId(roleId, response.data.getPermissionsByRoleId);
						})
						.catch(e => {
							const formattedError = formatGraphQlError(e);

							if (formattedError) {
								logger[formattedError.code === FORBIDDEN ? 'warn' : 'error'](
									{ error: formattedError, roleId: roleId },
									'Failed to get permissions by role'
								);
							} else {
								logger.error({ error: e, roleId: roleId }, 'Failed to get permissions by role');
							}
							this.setPermissionsByRoleId(roleId, null);
						});
				}
				return this.allPermissionsByRole[roleId];
			};
		},

		// Returns an array of all of the roles
		allRoles() {
			if (this.roleDefs && this.roleDefs.length === 0) {
				apollo
					.query({
						query: GET_ALL_ROLES
					})
					.then(response => {
						this.setRoleDefs(response.data.getAllRoles);
					})
					.catch(e => {
						const formattedError = formatGraphQlError(e);
						if (formattedError) {
							logger[formattedError.code === FORBIDDEN ? 'warn' : 'error']({ error: formattedError }, 'Failed to get all roles');
						} else {
							logger.error({ error: e }, 'Failed to get all roles');
						}
						this.setRoleDefs('');
					});
			}
			return this.roleDefs;
		},

		// Checks a permission against all roles tied to this user.
		hasGeneralPermission() {
			return permissionName => {
				// Just go through all roles that we know of in here
				for (const roleId in this.allPermissionsByRole) {
					if (this.allPermissionsByRole.hasOwnProperty(roleId)) {
						if (this.allPermissionsByRole[roleId].indexOf(permissionName) > -1) {
							// We found a case where they have the permission - why go further?
							return true;
						}
					}
				}

				// You are NOT WORTHY
				return false;
			};
		},

		// Checks a permission against the given role's list of permissions. Returns true if allowed; false if not.
		hasRolePermission() {
			return (permissionName, roleId) => {
				if (this.allPermissionsByRole[roleId] && this.allPermissionsByRole[roleId].indexOf(permissionName) > -1) {
					return true;
				}

				// You are NOT WORTHY
				return false;
			};
		}
	},
	methods: {
		// Tries to pull the permissions data from localStorage so we have something to work with before re-fetching
		loadLocalPermissions() {
			const permsStr = localStorage.getItem(LOCAL_STORAGE_PERMISSIONS);

			if (permsStr) {
				this.$set(this, 'allPermissionsByRole', JSON.parse(permsStr));
			}
		},

		// Initializes our permissions data for all roles that appear to be tied to this user
		initializeServerPermissions(user) {
			// Gather all roles that this user has tied to them
			const allRoles = [];
			allRoles.push(user.role); // Usually just customer, but could be something else, too

			for (const entry of user.storeRoles) {
				// Compile a de-duplicated array of all of their roles
				if (allRoles.indexOf(entry.role) === -1) {
					allRoles.push(entry.role);
				}
			}

			// Load the permissions (but the module calling this doesn't need to wait)
			for (const role of allRoles) {
				this.permissionsByRole(role);
			}

			// After we've fetched the data, make sure to REMOVE any permissions that MIGHT have been there before
			Object.keys(this.allPermissionsByRole).forEach(roleId => {
				if (allRoles.indexOf(roleId) === -1) {
					// Perhaps this user lost a role (and therefore permissions) since last login - don't let them pretend!
					this.removePermissionsByRoleId(roleId);
				}
			});
		},

		setPermissionsByRoleId(roleId, permissions) {
			// Cache it in our internal map AND in local storage
			this.$set(this.allPermissionsByRole, roleId, permissions);
			window.localStorage.setItem(LOCAL_STORAGE_PERMISSIONS, JSON.stringify(this.allPermissionsByRole));
		},

		removePermissionsByRoleId(id) {
			this.$delete(this.allPermissionsByRole, id);
			window.localStorage.setItem(LOCAL_STORAGE_PERMISSIONS, JSON.stringify(this.allPermissionsByRole));
		},

		setRoleDefs(roles) {
			// Cache it in our internal map AND in local storage
			this.$set(this, 'roleDefs', roles);
			window.localStorage.setItem(LOCAL_STORAGE_ROLES, roles ? JSON.stringify(this.roleDefs) : '');
		},

		// Wipes everything out that we have available directly through our props
		clearAll() {
			window.localStorage.removeItem(LOCAL_STORAGE_PERMISSIONS);
			window.localStorage.removeItem(LOCAL_STORAGE_ROLES);
			this.$set(this, 'allPermissionsByRole', {});
			this.$set(this, 'roleDefs', []);
		}
	},
	created() {
		// Pre-load our local permissions, if possible, so that there's no waiting to know what the user can do
		this.loadLocalPermissions();
	}
});

export default Role;
